// Simplified Permission Configuration
export const permissionConfig = {
  // Core modules with user-friendly names and descriptions
  modules: {
    dashboard: {
      name: 'Dashboard',
      description: 'View system overview and analytics',
      icon: '📊',
      permissions: [
        { key: 'dashboard_view', name: 'View Dashboard', description: 'Access main dashboard' }
      ]
    },
    products: {
      name: 'Products',
      description: 'Manage product catalog',
      icon: '📦',
      permissions: [
        { key: 'products_view', name: 'View Products', description: 'View product listings and details' },
        { key: 'products_create', name: 'Create Products', description: 'Add new products to the catalog' },
        { key: 'products_update', name: 'Update Products', description: 'Modify existing product details' },
        { key: 'products_edit', name: 'Edit Products', description: 'Edit product information' },
        { key: 'products_delete', name: 'Delete Products', description: 'Remove products from catalog' }
      ]
    },
    categories: {
      name: 'Categories',
      description: 'Manage product categories',
      icon: '📂',
      permissions: [
        { key: 'categories_view', name: 'View Categories', description: 'View category listings and details' },
        { key: 'categories_create', name: 'Create Categories', description: 'Add new product categories' },
        { key: 'categories_update', name: 'Update Categories', description: 'Modify existing category details' },
        { key: 'categories_edit', name: 'Edit Categories', description: 'Edit category information' },
        { key: 'categories_delete', name: 'Delete Categories', description: 'Remove categories from system' }
      ]
    },
    companies: {
      name: 'Companies',
      description: 'Manage company names and associations',
      icon: '🏢',
      permissions: [
        { key: 'companies_view', name: 'View Companies', description: 'View company listings' },
        { key: 'companies_create', name: 'Add Companies', description: 'Create new companies' },
        { key: 'companies_update', name: 'Edit Companies', description: 'Modify existing companies' },
        { key: 'companies_delete', name: 'Delete Companies', description: 'Remove companies' }
      ]
    },
    product_groups: {
      name: 'Product Groups',
      description: 'Manage product groupings',
      icon: '🗂️',
      permissions: [
        { key: 'product_groups_view', name: 'View Product Groups', description: 'View product group listings' },
        { key: 'product_groups_create', name: 'Create Product Groups', description: 'Create new product groups' },
        { key: 'product_groups_update', name: 'Update Product Groups', description: 'Modify existing product groups' },
        { key: 'product_groups_delete', name: 'Delete Product Groups', description: 'Remove product groups' }
      ]
    },
    orders: {
      name: 'Orders',
      description: 'Handle customer orders',
      icon: '🛍️',
      permissions: [
        { key: 'orders_view', name: 'View Orders', description: 'View order listings' },
        { key: 'orders_create', name: 'Create Orders', description: 'Create new orders' },
        { key: 'orders_update', name: 'Update Orders', description: 'Modify order status' },
        { key: 'orders_delete', name: 'Delete Orders', description: 'Remove orders' }
      ]
    },
    customers: {
      name: 'Customers',
      description: 'Manage customer accounts',
      icon: '👥',
      permissions: [
        { key: 'customers_view', name: 'View Customers', description: 'View customer listings' },
        { key: 'customers_create', name: 'Add Customers', description: 'Create new customers' },
        { key: 'customers_update', name: 'Edit Customers', description: 'Modify customer details' },
        { key: 'customers_delete', name: 'Delete Customers', description: 'Remove customers' }
      ]
    },
    employees: {
      name: 'Employees',
      description: 'Manage staff and permissions',
      icon: '👤',
      permissions: [
        { key: 'employees_view', name: 'View Employees', description: 'View employee listings and details' },
        { key: 'employees_create', name: 'Create Employees', description: 'Add new employees to system' },
        { key: 'employees_update', name: 'Update Employees', description: 'Modify employee details and roles' },
        { key: 'employees_edit', name: 'Edit Employees', description: 'Edit employee information' },
        { key: 'employees_delete', name: 'Delete Employees', description: 'Remove employees from system' },
        { key: 'employees_assign_view', name: 'View Permission Assignments', description: 'View employee permission assignments' },
        { key: 'employees_assign_add', name: 'Add Permission Assignments', description: 'Add new permissions to employees' },
        { key: 'employees_assign_delete', name: 'Delete Permission Assignments', description: 'Remove permissions from employees' }
      ]
    },
    subscriptions: {
      name: 'Subscriptions',
      description: 'Manage subscription services',
      icon: '📅',
      permissions: [
        { key: 'subscriptions_view', name: 'View Subscriptions', description: 'View subscription listings' },
        { key: 'subscriptions_create', name: 'Add Subscriptions', description: 'Create new subscriptions' },
        { key: 'subscriptions_update', name: 'Edit Subscriptions', description: 'Modify subscriptions' },
        { key: 'subscriptions_delete', name: 'Delete Subscriptions', description: 'Remove subscriptions' }
      ]
    },
    notifications: {
      name: 'Notifications',
      description: 'Manage system notifications',
      icon: '🔔',
      permissions: [
        { key: 'notifications_view', name: 'View Notifications', description: 'View notifications' },
        { key: 'notifications_create', name: 'Send Notifications', description: 'Create notifications' },
        { key: 'notifications_update', name: 'Edit Notifications', description: 'Modify notifications' },
        { key: 'notifications_delete', name: 'Delete Notifications', description: 'Remove notifications' }
      ]
    },
    settings: {
      name: 'Settings',
      description: 'System configuration',
      icon: '⚙️',
      permissions: [
        { key: 'settings_view', name: 'View Settings', description: 'View system settings' },
        { key: 'settings_update', name: 'Edit Settings', description: 'Modify system settings' }
      ]
    },
    permissions: {
      name: 'Permissions',
      description: 'Manage user permissions and access control',
      icon: '🔐',
      permissions: [
        { key: 'permissions_view', name: 'View Permissions', description: 'View permission settings and assignments' },
        { key: 'permissions_create', name: 'Create Permissions', description: 'Create new system permissions' },
        { key: 'permissions_update', name: 'Update Permissions', description: 'Modify existing permissions' },
        { key: 'permissions_edit', name: 'Edit Permissions', description: 'Edit permission details' },
        { key: 'permissions_delete', name: 'Delete Permissions', description: 'Remove permissions from system' }
      ]
    },
    delivery_boy: {
      name: 'Delivery Boy',
      description: 'Manage delivery personnel and order assignments',
      icon: '🚴',
      permissions: [
        { key: 'delivery_boy_access', name: 'Access Delivery Boy Module', description: 'Access the delivery boy module' },
        { key: 'delivery_boy_view', name: 'View Delivery Boys', description: 'View delivery boy information and orders' },
        { key: 'delivery_boy_assign', name: 'Assign Orders', description: 'Assign orders to delivery boys' },
        { key: 'delivery_boy_manage', name: 'Manage Delivery Boys', description: 'Manage delivery boy profiles and settings' }
      ]
    },
    delivery_orders: {
      name: 'Delivery Orders',
      description: 'Manage daily delivery operations',
      icon: '🚚',
      permissions: [
        { key: 'delivery_orders_view', name: 'View Delivery Orders', description: 'View delivery orders and schedules' },
        { key: 'delivery_orders_manage', name: 'Manage Delivery Orders', description: 'Manage delivery orders and routes' }
      ]
    },
    scheduler: {
      name: 'Scheduler',
      description: 'Manage automated tasks and cron jobs',
      icon: '⏰',
      permissions: [
        { key: 'scheduler_view', name: 'View Scheduler', description: 'View scheduled tasks and automation' },
        { key: 'scheduler_manage', name: 'Manage Scheduler', description: 'Manage scheduled tasks and automation' }
      ]
    }
  },

  // Predefined role templates for quick assignment
  roleTemplates: {
    developer: {
      name: 'Developer (Super Admin)',
      description: 'Ultimate system access - can do everything including assign permissions',
      permissions: [
        // ALL PERMISSIONS - Developer has everything
        'dashboard_view',
        'products_view', 'products_create', 'products_update', 'products_edit', 'products_delete',
        'categories_view', 'categories_create', 'categories_update', 'categories_edit', 'categories_delete',
        'companies_view', 'companies_create', 'companies_update', 'companies_delete',
        'product_groups_view', 'product_groups_create', 'product_groups_update', 'product_groups_delete',
        'orders_view', 'orders_create', 'orders_update', 'orders_delete',
        'customers_view', 'customers_create', 'customers_update', 'customers_delete',
        'employees_view', 'employees_create', 'employees_update', 'employees_edit', 'employees_delete',
        'employees_assign_view', 'employees_assign_add', 'employees_assign_delete',
        'subscriptions_view', 'subscriptions_create', 'subscriptions_update', 'subscriptions_delete',
        'notifications_view', 'notifications_create', 'notifications_update', 'notifications_delete',
        'settings_view', 'settings_update',
        'permissions_view', 'permissions_create', 'permissions_update', 'permissions_edit', 'permissions_delete',
        'delivery_boy_access', 'delivery_boy_view', 'delivery_boy_assign', 'delivery_boy_manage',
        'delivery_orders_view', 'delivery_orders_manage',
        'scheduler_view', 'scheduler_manage'
      ]
    },
    admin: {
      name: 'Administrator',
      description: 'Full access to most features - may have permission assignment rights if granted',
      permissions: [
        'dashboard_view',
        'products_view', 'products_create', 'products_update', 'products_delete',
        'categories_view', 'categories_create', 'categories_update', 'categories_delete',
        'companies_view', 'companies_create', 'companies_update', 'companies_delete',
        'product_groups_view', 'product_groups_create', 'product_groups_update', 'product_groups_delete',
        'orders_view', 'orders_create', 'orders_update', 'orders_delete',
        'customers_view', 'customers_create', 'customers_update', 'customers_delete',
        'employees_view', 'employees_create', 'employees_update', 'employees_delete',
        'subscriptions_view', 'subscriptions_create', 'subscriptions_update', 'subscriptions_delete',
        'notifications_view', 'notifications_create', 'notifications_update', 'notifications_delete',
        'settings_view', 'settings_update',
        'permissions_view',
        'delivery_boy_access', 'delivery_boy_view', 'delivery_boy_assign', 'delivery_boy_manage',
        'delivery_orders_view', 'delivery_orders_manage',
        'scheduler_view', 'scheduler_manage'
        // Note: employees_assign_* permissions can be granted separately by Developer
      ]
    },
    manager: {
      name: 'Manager',
      description: 'Management level access with editing capabilities',
      permissions: [
        'dashboard_view',
        'products_view', 'products_create', 'products_update',
        'categories_view', 'categories_create', 'categories_update',
        'companies_view', 'companies_create', 'companies_update',
        'product_groups_view', 'product_groups_create', 'product_groups_update',
        'orders_view', 'orders_update',
        'customers_view', 'customers_create', 'customers_update',
        'employees_view',
        'subscriptions_view', 'subscriptions_create', 'subscriptions_update',
        'notifications_view', 'notifications_create',
        'delivery_boy_view', 'delivery_boy_assign',
        'delivery_orders_view'
        // Note: employees_assign_* permissions can be granted separately by Developer/Admin
      ]
    },
    staff: {
      name: 'Staff Member',
      description: 'Basic staff access - view only for most features',
      permissions: [
        'dashboard_view',
        'products_view',
        'categories_view',
        'companies_view',
        'product_groups_view',
        'orders_view',
        'customers_view'
      ]
    }
  },

  // Get user-friendly permission name
  getPermissionName: function(permissionKey) {
    for (const module of Object.values(this.modules)) {
      const permission = module.permissions.find(p => p.key === permissionKey);
      if (permission) return permission.name;
    }
    return permissionKey;
  },

  // Get module info for permission
  getModuleForPermission: function(permissionKey) {
    for (const [moduleKey, module] of Object.entries(this.modules)) {
      if (module.permissions.some(p => p.key === permissionKey)) {
        return { key: moduleKey, ...module };
      }
    }
    return null;
  },

  // Get all permissions for a module
  getModulePermissions: function(moduleKey) {
    return this.modules[moduleKey]?.permissions || [];
  },

  // Simple Permission Assignment Helper Functions
  canUserAssignPermissions: function(userPermissions) {
    // Check if user has all required assignment permissions
    const requiredPermissions = [
      'employees_assign_view',    // Must see assignment interface
      'employees_assign_add',     // Must be able to add permissions
      'employees_assign_delete'   // Must be able to remove permissions
    ];
    
    return requiredPermissions.every(permission => userPermissions.includes(permission));
  },

  isDeveloper: function(userPermissions) {
    // Check if user is Developer (has all core permissions including permission creation)
    const developerPermissions = [
      'permissions_create', 
      'permissions_delete', 
      'employees_assign_add', 
      'employees_assign_delete'
    ];
    return developerPermissions.every(permission => userPermissions.includes(permission));
  },

  getAllPermissionKeys: function() {
    // Get all permission keys for Developer role
    const allPermissions = [];
    for (const module of Object.values(this.modules)) {
      for (const permission of module.permissions) {
        allPermissions.push(permission.key);
      }
    }
    return allPermissions;
  }
};